import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback } from 'react'
import { toast } from 'sonner'

import { useBillingClient } from './use-billing-client.hook'
import { useSeatsApi } from './use-seats-api.hook'

const getErrorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback

interface UseAddSeatsParams {
  setIsAddingSeats: (isAdding: boolean) => void
  fetchSeats: () => Promise<void>
}

export const useAddSeats = ({ setIsAddingSeats, fetchSeats }: UseAddSeatsParams) => {
  const { addSeats: addSeatsApi } = useSeatsApi()
  const { refetchTenant } = useBillingClient()

  const addSeats = useCallback(
    async (quantity: number): Promise<boolean> => {
      setIsAddingSeats(true)
      try {
        const response = await addSeatsApi(quantity)

        if (response.paymentPending && response.paymentUrl) {
          window.location.href = response.paymentUrl
          return true
        }

        toast.success(`Successfully added ${quantity} ${quantity === 1 ? 'seat' : 'seats'}`)
        trackEvent('seats_added', { quantity })
        await Promise.all([fetchSeats(), refetchTenant()])
        return true
      } catch (err) {
        const message = getErrorMessage(err, 'Failed to add seats')
        toast.error(message)
        logger.error('Failed to add seats', { error: err, quantity })
        return false
      } finally {
        setIsAddingSeats(false)
      }
    },
    [addSeatsApi, fetchSeats, refetchTenant, setIsAddingSeats]
  )

  return { addSeats }
}
